import {
  CultivationRecord,
  Fertilizer,
  FuelUse,
  LandUsageChange,
  Pesticide
} from "../../../../../types/prodcut-data-review/product-data.types";

export const mainFieldDefaults = (rec: CultivationRecord | undefined) => ({
  cropName: rec?.cropName || "",
  dataType: rec?.dataType || "",
  produceType: rec?.produceType !== "" && rec?.produceType != null ? String(rec.produceType) : "",
  originCountry:
    rec?.originCountry !== "" && rec?.originCountry != null ? String(rec.originCountry) : "",
  harvestYear: rec?.harvestYear || "",
  fieldName: rec?.fieldName || "",
  area: rec?.area || "",
  yieldAmount: rec?.yieldAmount || "",
  yieldUnit: rec?.yieldUnit || "kg",
  notes: rec?.notes || ""
});

/** Generic id→label lookup against a static-data list ({key,text} shape). */
export const lookupText = (list: any[], id: number | string) =>
  list.find((x: any) => String(x.key) === String(id))?.text ?? `#${id}`;

// ── Draft-item factories ─────────────────────────────────────────────────────

export const createFertilizer = (): Fertilizer => ({ id: crypto.randomUUID(), fertilizerQuantity: "" });

export const createPesticide = (): Pesticide => ({ id: Date.now(), pesticideQuantity: "" });

export const createFuelUse = (): FuelUse => ({
  id: crypto.randomUUID(),
  energySource: 0,
  energyUsed: 0,
  energyUnit: 0
});

export const createLandUsageChange = (): LandUsageChange => ({
  id: crypto.randomUUID(),
  landUsageFrom: "",
  landUsageTo: "",
  landUseYearOfChange: "",
  landUsageAllocation: ""
});

// ── Save payload builders ────────────────────────────────────────────────────

export const buildFertilizerPayload = (drafts: Fertilizer[]) =>
  drafts
    .map(f => ({ id: Number(f.id), fertilizerQuantity: f.fertilizerQuantity }))
    .filter(f => !Number.isNaN(f.id));

export const buildPesticidePayload = (drafts: Pesticide[]) =>
  drafts
    .map(p => ({ id: Number(p.id), pesticideQuantity: p.pesticideQuantity }))
    .filter(p => !Number.isNaN(p.id));

export const buildFuelUsePayload = (drafts: FuelUse[]) =>
  drafts.map(f => ({
    energySource: f.energySource,
    energyUsed: f.energyUsed,
    energyUnit: f.energyUnit
  }));

export const buildLandUsagePayload = (drafts: LandUsageChange[]) =>
  drafts.map(l => ({
    landUsageFrom: l.landUsageFrom,
    landUsageTo: l.landUsageTo,
    landUseYearOfChange: l.landUseYearOfChange,
    landUsageAllocation: l.landUsageAllocation
  }));

interface CultivationFieldDrafts {
  fertilizerList: Fertilizer[];
  pesticideList: Pesticide[];
  fuelUse: FuelUse[];
  landUsage: LandUsageChange[];
}

/** Builds the `onChange` patch after a successful save, merging the main-field form
 * values with the four draft lists back onto the existing `cultivationFields`. */
export const buildCultivationFieldPatch = (
  values: ReturnType<typeof mainFieldDefaults>,
  cf: CultivationRecord["cultivationFields"] | undefined,
  drafts: CultivationFieldDrafts
): Partial<CultivationRecord> => ({
  cropName: values.cropName,
  fieldName: values.fieldName,
  dataType: values.dataType,
  produceType: values.produceType !== "" ? Number(values.produceType) : "",
  originCountry: values.originCountry !== "" ? Number(values.originCountry) : "",
  harvestYear: values.harvestYear,
  area: values.area !== "" ? Number(values.area) : "",
  yieldAmount: values.yieldAmount !== "" ? Number(values.yieldAmount) : "",
  notes: values.notes,
  cultivationFields: {
    ...cf,
    fertilizerList: drafts.fertilizerList,
    pesticideList: drafts.pesticideList,
    fuelUse: drafts.fuelUse,
    landUsage: drafts.landUsage
  } as any
});
